import type React from "react";
import type { ClientApplication } from "@shopify/app-bridge";
import { Redirect, Toast } from "@shopify/app-bridge/actions";
import { getSessionToken } from "@shopify/app-bridge-utils";
import type { HostAPI } from "../HostAPI";

export async function startShopifySubscription(planId?: string): Promise<string> {
  const response = await fetch("/api/shopify/billing/subscribe", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ planId: planId ?? null }),
  });

  const raw = await response.text().catch(() => "");
  let data: any = null;
  if (raw) {
    try {
      data = JSON.parse(raw);
    } catch {
      data = null;
    }
  }

  if (!response.ok) {
    let message = "Failed to start Shopify billing subscription";
    if (data?.error) message = data.error;
    else if (typeof data?.message === "string") message = data.message;
    else if (raw) message = raw;
    throw new Error(message);
  }

  const confirmationUrl = data?.confirmationUrl;
  if (!confirmationUrl) {
    throw new Error("Missing confirmationUrl from billing subscribe response");
  }

  return confirmationUrl;
}

export class ShopifyHostAPI implements HostAPI {
  private app: ClientApplication;
  private readonly originalFetch: typeof window.fetch | null;
  private fetchPatched = false;

  constructor(app: ClientApplication) {
    this.app = app;
    this.originalFetch = typeof window !== "undefined" ? window.fetch.bind(window) : null;
    this.patchGlobalFetch();
  }

  async getSessionToken(): Promise<string | null> {
    try {
      const token = await getSessionToken(this.app);
      return token || null;
    } catch (error) {
      console.error("[shopify] failed to get session token", error);
      return null;
    }
  }

  toast(message: string): void {
    try {
      const toast = Toast.create(this.app, { message, duration: 3000 });
      toast.dispatch(Toast.Action.SHOW);
    } catch (error) {
      console.warn("[shopify] toast fallback", error);
      console.log(`[Shopify toast] ${message}`);
    }
  }

  openModal(_node: React.ReactNode): void {
    // TODO: Integrate Shopify modal or resource picker in future iteration.
  }

  navigate(path: string): void {
    const nextPath = this.ensureEmbeddedParams(path);
    try {
      const redirect = Redirect.create(this.app);
      const isAbsolute = /^https?:\/\//i.test(nextPath);
      if (isAbsolute) {
        redirect.dispatch(Redirect.Action.REMOTE, nextPath);
      } else {
        redirect.dispatch(Redirect.Action.APP, nextPath);
      }
    } catch (error) {
      console.warn("[shopify] redirect fallback", error);
      if (typeof window !== "undefined") {
        window.location.assign(nextPath);
      }
    }
  }

  async fetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    return this.fetchWithAuth(input, init);
  }

  async createSubscription(planId?: string): Promise<void> {
    const confirmationUrl = await startShopifySubscription(planId);
    this.navigate(confirmationUrl);
  }

  async cancelSubscription(): Promise<void> {
    const response = await this.fetchWithAuth("/api/billing/shopify/cancel", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || "Failed to cancel Shopify subscription");
    }
  }

  private async fetchWithAuth(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    if (!this.originalFetch) {
      throw new Error("window.fetch is unavailable in this environment");
    }

    const token = await this.getSessionToken();
    const headers = new Headers(init?.headers || {});
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    headers.set("X-Shopify-Embedded", "1");

    const nextInit: RequestInit = {
      ...init,
      credentials: "include",
      headers,
    };

    return this.originalFetch(input as RequestInfo, nextInit);
  }

  private patchGlobalFetch(): void {
    if (typeof window === "undefined" || this.fetchPatched || !this.originalFetch) return;

    const shouldAuthorize = (input: RequestInfo | URL): boolean => {
      if (input instanceof Request) {
        return this.shouldAuthorizeUrl(input.url);
      }
      if (typeof input === "string") {
        return this.shouldAuthorizeUrl(input);
      }
      if (input instanceof URL) {
        return this.shouldAuthorizeUrl(input.toString());
      }
      return false;
    };

    const originalFetch = this.originalFetch;
    const fetchWithAuth = this.fetchWithAuth.bind(this);

    window.fetch = function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
      if (shouldAuthorize(input)) {
        return fetchWithAuth(input, init);
      }
      return originalFetch(input as RequestInfo, init);
    } as typeof window.fetch;

    this.fetchPatched = true;
  }

  private shouldAuthorizeUrl(url: string): boolean {
    if (url.startsWith("/")) {
      return true;
    }
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.origin === window.location.origin;
    } catch {
      return false;
    }
  }

  private ensureEmbeddedParams(target: string): string {
    if (/^https?:\/\//i.test(target)) {
      return target;
    }
    if (typeof window === "undefined") return target;
    const [pathPart, existingQuery = ""] = target.split("?");
    const currentParams = new URLSearchParams(window.location.search);
    const hasShop = currentParams.has("shop");
    const hasHost = currentParams.has("host");
    const isEmbeddedRoute = window.location.pathname.startsWith("/embedded");
    const hasEmbedded = currentParams.get("embedded") === "1" || isEmbeddedRoute;
    if (!hasShop && !hasHost && !hasEmbedded) {
      return target;
    }

    const nextParams = new URLSearchParams(existingQuery);
    if (hasShop && !nextParams.has("shop")) {
      const value = currentParams.get("shop");
      if (value) nextParams.set("shop", value);
    }
    if (hasHost && !nextParams.has("host")) {
      const value = currentParams.get("host");
      if (value) nextParams.set("host", value);
    }
    if (hasEmbedded && !nextParams.has("embedded")) {
      nextParams.set("embedded", "1");
    }

    const query = nextParams.toString();
    return query ? `${pathPart}?${query}` : pathPart;
  }
}
